import { useQuery } from '@tanstack/react-query';

import { USER_QUERY_KEYS, UserEndpoints } from '@xemplo/gp-constants';
import {
  BaseUserExtended,
  ExpedoUserProfile,
  ListPagedResult,
  PagedRequest,
  PersonaRole,
} from '@xemplo/gp-types';
import { useHttpClient } from '@xemplo/http';
import { apiUrlBuilder } from '@xemplo/url-helper';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { UserListQueryProps } from './users-query.types';

export const useGetUserList = (props: UserListQueryProps) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams, roleCode, getPayrollManagers } = props;

  const endpoint = getUsersEndpoint(roleCode, getPayrollManagers);
  const url = apiUrlBuilder(endpoint, applicationApiUrl, requestParams);

  return useQuery<ListPagedResult<ExpedoUserProfile>>({
    queryKey: [USER_QUERY_KEYS.GET_USER_LIST, url, roleCode],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<ExpedoUserProfile>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!(applicationApiUrl && roleCode),
  });
};

export const useGetCompanyUsersQueryV2 = (props: { requestParams?: PagedRequest }) => {
  const { applicationApiUrl } = useAppSettings();
  const { client } = useHttpClient();
  const { requestParams } = props;
  const url = apiUrlBuilder(UserEndpoints.listUsers, applicationApiUrl, requestParams);

  return useQuery<ListPagedResult<BaseUserExtended>>({
    queryKey: [USER_QUERY_KEYS.USERS, url],
    queryFn: ({ signal }) =>
      client
        .get<ListPagedResult<BaseUserExtended>>(url, { signal })
        .then((resp) => resp.data),
    enabled: !!applicationApiUrl,
  });
};

function getUsersEndpoint(
  roleCode: PersonaRole | undefined,
  getPayrollManagers?: boolean,
) {
  switch (roleCode) {
    case PersonaRole.PSPAdmin:
    case PersonaRole.SystemAdmin:
      return getPayrollManagers
        ? UserEndpoints.listPSPAServiceProviderUsers
        : UserEndpoints.listPSPACompanyUsers;

    case PersonaRole.PayrollManager:
      return UserEndpoints.listPSPACompanyUsers;

    default:
      return UserEndpoints.listCompanyUsers;
  }
}
